import React, { useState, useEffect } from 'react';
import WidgetWrapper from './WidgetWrapper';

const PipelineWidget = ({ darkMode = false, onError }) => {
  const [stats, setStats] = useState({
    totalDeals: 0,
    totalValue: 0,
    draftsPending: 0,
    emailsSent: 0,
    replies: 0,
    bounces: 0,
    pipelineActive: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, prospectingRes] = await Promise.all([
          fetch('/api/sales/pipeline-summary', { credentials: 'include' }).then(r => r.json()),
          fetch('/api/prospecting/status', { credentials: 'include' }).then(r => r.json()).catch(() => ({})),
        ]);

        const summary = summaryRes.data || summaryRes;
        const stagesObj = summary.stages || {};
        const stages = Object.values(stagesObj);
        const totalDeals = stages.reduce((sum, s) => sum + (s.count || 0), 0);
        const totalValue = stages.reduce((sum, s) => sum + (s.weighted_value || s.total_value || s.value || 0), 0);

        setStats({
          totalDeals,
          totalValue,
          draftsPending: prospectingRes.drafts_pending || 0,
          emailsSent: prospectingRes.drafts_sent || 0,
          replies: prospectingRes.replies || 0,
          bounces: 0,
          pipelineActive: prospectingRes.is_active || false,
        });
      } catch (e) {
        setError(e.message);
        if (onError) onError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [onError]);

  const formatCurrency = (n) => {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
    return `$${Math.round(n)}`;
  };

  return (
    <WidgetWrapper title="📊 Sales Pipeline" loading={loading} error={error} darkMode={darkMode} className="pipeline-widget">
      {(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 800;
        const gridStyle = {
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '6px' : '12px',
        };
        const cardStyle = {
          padding: isMobile ? '6px 8px' : '10px 12px',
          borderRadius: '6px',
          backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
          border: `1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`,
        };
        const labelStyle = {
          fontSize: isMobile ? '10px' : '11px',
          color: darkMode ? '#94a3b8' : '#64748b',
          marginBottom: '2px',
        };
        const valueStyle = {
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: '700',
          color: darkMode ? '#e2e8f0' : '#1e293b',
        };
        const badgeStyle = (color) => ({
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: '600',
          backgroundColor: color,
          color: '#fff',
        });

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Status indicator */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
                {stats.pipelineActive ? '🟢 Prospecting Active' : '⚪ Prospecting Paused'}
              </span>
              {stats.draftsPending > 0 && (
                <span style={badgeStyle('#1e3a8a')}>📝 {stats.draftsPending} drafts to review</span>
              )}
            </div>

            {/* Stats grid */}
            <div style={gridStyle}>
              <div style={cardStyle}>
                <div style={labelStyle}>Active Deals</div>
                <div style={valueStyle}>{stats.totalDeals}</div>
              </div>
              <div style={cardStyle}>
                <div style={labelStyle}>Pipeline Value</div>
                <div style={valueStyle}>{formatCurrency(stats.totalValue)}</div>
              </div>
              <div style={cardStyle}>
                <div style={labelStyle}>Emails Sent</div>
                <div style={valueStyle}>{stats.emailsSent}</div>
              </div>
              <div style={cardStyle}>
                <div style={labelStyle}>Drafts Pending</div>
                <div style={{ ...valueStyle, color: stats.draftsPending > 0 ? '#f59e0b' : (darkMode ? '#e2e8f0' : '#1e293b') }}>
                  {stats.draftsPending}
                </div>
              </div>
              <div style={cardStyle}>
                <div style={labelStyle}>Replies</div>
                <div style={{ ...valueStyle, color: stats.replies > 0 ? '#10b981' : (darkMode ? '#e2e8f0' : '#1e293b') }}>
                  {stats.replies}
                </div>
              </div>
              <div style={cardStyle}>
                <div style={labelStyle}>Bounces</div>
                <div style={{ ...valueStyle, color: stats.bounces > 0 ? '#ef4444' : (darkMode ? '#e2e8f0' : '#1e293b') }}>
                  {stats.bounces}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </WidgetWrapper>
  );
};

PipelineWidget.widgetId = 'pipeline';
PipelineWidget.defaultSize = { cols: 6 };

export default PipelineWidget;